import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { cloneDeep, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as action from '../store/CareLabel.action';
import { checkArray, imageUrl } from '../../../../Components/helper';

const initialState = {
  pageLoader: false,
  CareLabelList: [],
  CareGroupData: [],
  activeCareType: null,
  imgPath: imageUrl,
  selectedLibrary: [],
  pageType: '',
  careTypeTabs: [
    { id: 'symbol', label: 'Symbols', isActive: false },
    { id: 'instruction', label: 'Instruction', isActive: false },
    { id: 'disclaimer', label: 'Disclaimer', isActive: false },
  ],
  addLibraryTemplate: {
    image: '',
    fullImage: '',
    code: '',
    description: '',
    type: '',
    editMode: true,
    newItem: true,
    errors: [],
  },
  editedId: '',
  uploadingData: false,
  libSearchText: '',
  pageIndex: 1,
  pageSize: 20,
  hasMore: false,
  totalCount: 0,
};

const { careTypeTabs } = initialState;

export const StateContext = createContext(null);

const careTypeOption = [
  { id: 'Wash', label: 'Wash' },
  { id: 'Bleach', label: 'Bleach' },
  { id: 'Dry', label: 'Dry' },
  { id: 'Iron', label: 'Iron' },
  { id: 'Professional care', label: 'Professional care' },
  { id: 'Other', label: 'Other' },
];
const disclaimerTypeOption = [
  { id: 'Wash', label: 'Wash' },
  { id: 'Caution', label: 'Caution' },
  { id: 'Warning', label: 'Warning' },
  { id: 'Appearance', label: 'Appearance' },
  { id: 'Animal Parts', label: 'Animal Parts' },
  { id: 'Other', label: 'Other' },
];

export default function CareLabelStateProvider({ children }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { careType } = useParams();
  const pathName = history.location.pathname;
  const { t } = useTranslation('common');
  const {
    CareLabelReducer: { CareLibraryList, CareGropList },
    UserDetails: { userInfo },
  } = useSelector((reduxState) => reduxState);

  const [careLabelState, setCareLabelState] = useState({ ...initialState });

  const { CareLabelList, selectedLibrary, addLibraryTemplate, activeCareType, pageIndex, pageSize, totalCount, libSearchText } = careLabelState;

  const getCurrentLibraryData = async () => {
    const typeObj = {
      type: careType,
      pageSize,
      pageIndex: 1,
    };
    const response = await dispatch(action.fetchCareLabel(typeObj));

    setCareLabelState((prevState) => ({
      ...prevState,
      pageIndex: 1,
      totalCount: response?.data?.totalCount,
    }));
    return response;
  };

  const getCareGroupData = async (searchText) => {
    return dispatch(action.fetchCareLabelGroup(searchText));
  };

  useEffect(() => {
    if (careType) {
      const callFn = async () => {
        setCareLabelState((prevState) => ({
          ...prevState,
          pageLoader: true,
          CareLabelList: [],
        }));
        const updatedArray = careTypeTabs.map((item) => {
          if (item.id === careType) {
            return { ...item, isActive: true };
          }
          return item;
        });

        const updateNewObj = careType === 'disclaimer' ? { disclaimerType: '' } : { careType: '' };
        const response = await getCurrentLibraryData();
        if (response?.code === 200) {
          setCareLabelState((prevState) => ({
            ...prevState,
            careTypeTabs: updatedArray,
            activeCareType: careType,
            pageLoader: false,
            selectedLibrary: [],
            pageType: 'Library',
            libSearchText: '',
            addLibraryTemplate: { ...initialState.addLibraryTemplate, ...updateNewObj, type: careType },
            totalCount: response?.data?.totalCount,
          }));
        }
      };

      callFn();
    }
  }, [dispatch, careType]);

  useEffect(() => {
    if (checkArray(CareLibraryList)) {
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: [...CareLabelList, ...CareLibraryList],
      }));
    }
  }, [CareLibraryList]);

  useEffect(() => {
    if (CareLabelList?.length > 0 && CareLabelList.length === totalCount) {
      setCareLabelState((prevState) => ({
        ...prevState,
        hasMore: false,
      }));
    } else {
      setCareLabelState((prevState) => ({
        ...prevState,
        hasMore: true,
      }));
    }
  }, [CareLabelList?.length]);

  useEffect(() => {
    const callFn = async () => {
      setCareLabelState((prevState) => ({
        ...prevState,
        pageLoader: true,
      }));
      getCareGroupData();
      setCareLabelState((prevState) => ({
        ...prevState,
        pageType: 'Group',
        pageLoader: false,
      }));
    };
    if (pathName.includes('care-group')) {
      callFn();
    }
  }, [pathName]);

  useEffect(() => {
    setCareLabelState((prevState) => ({
      ...prevState,
      CareGroupData: CareGropList,
    }));
  }, [CareGropList]);

  const handleCheckAll = async (e) => {
    if (e.target.checked) {
      const allIds = CareLabelList && checkArray(CareLabelList) && CareLabelList.map((ele) => ele._id);

      if (allIds) {
        setCareLabelState((prevState) => ({
          ...prevState,
          selectedLibrary: allIds.filter((element) => element !== undefined),
        }));
      }
    } else {
      setCareLabelState((prevState) => ({
        ...prevState,
        selectedLibrary: [],
      }));
    }
  };

  const handleCheckLibrary = async (isLibChecked, libId) => {
    const existingIds = selectedLibrary;
    if (isLibChecked && !existingIds.includes(libId)) {
      setCareLabelState((prevState) => ({
        ...prevState,
        selectedLibrary: [...existingIds, libId],
      }));
    } else {
      const removedArray = existingIds?.filter((eleId) => eleId !== libId);

      if (checkArray(removedArray)) {
        setCareLabelState((prevState) => ({
          ...prevState,
          selectedLibrary: removedArray,
        }));
      } else {
        setCareLabelState((prevState) => ({
          ...prevState,
          selectedLibrary: [],
        }));
      }
    }
  };

  const isallChecked = checkArray(CareLabelList) && selectedLibrary.length === CareLabelList.length;

  const handleAddLibrary = () => {
    const isNewItemExist = CareLabelList.filter((ele) => ele.newItem === true);
    if (isNewItemExist.length === 0) {
      const newArray = [addLibraryTemplate].concat(CareLabelList);
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: newArray,
      }));
    }
  };

  const handleClose = (ele) => {
    const activeType = activeCareType === 'disclaimer' ? 'disclaimerType' : 'careType';
    const itemObj = {
      image: '',
      fullImage: '',
      code: '',
      description: '',
      type: careType,
      editMode: true,
      newItem: true,
      errors: [],
    };
    itemObj[activeType] = '';
    if (ele.newItem) {
      const data = cloneDeep(CareLabelList);
      data.splice(0, 1);
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: data,
        addLibraryTemplate: itemObj,
      }));
    } else {
      const index = CareLabelList.findIndex((item) => item._id === ele._id);
      const dataList = CareLabelList;
      set(dataList, `[${index}][editMode]`, false);
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: dataList,
        addLibraryTemplate: itemObj,
      }));
    }
  };

  const handleInputChange = (inputVal, element, index) => {
    const dataList = CareLabelList;
    const keyName = inputVal.target.name;

    set(dataList, `[${index}][${keyName}]`, inputVal.target.value);
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: dataList,
    }));
  };

  const handleSelectChange = (selectValue, element, keyName, index) => {
    const dataList = CareLabelList;

    set(dataList, `[${index}][${keyName}]`, selectValue.id);
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: dataList,
    }));
  };

  const resetData = async () => {
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: [],
      addLibraryTemplate: {
        ...addLibraryTemplate,
        image: '',
        fullImage: '',
        code: '',
        description: '',
        errors: [],
        type: activeCareType,
      },
    }));
    getCurrentLibraryData();
  };

  const checkValidation = (element) => {
    const errors = [];
    const activeType = activeCareType === 'disclaimer' ? 'disclaimerType' : 'careType';

    if (element.code.trim() === '') {
      errors.push('code');
    }
    if (element.description.trim() === '') {
      errors.push('description');
    }
    if (element[activeType] === '') {
      errors.push(activeType);
    }

    let index = -1;
    if (element.newItem) {
      index = 0;
    } else {
      index = CareLabelList.findIndex((item) => item._id === element._id);
    }

    const dataList = CareLabelList;
    set(dataList, `[${index}][errors]`, errors);
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: dataList,
    }));
    if (errors.length === 0) {
      return true;
    }
  };

  const handleAddUpdate = async (element) => {
    const isValid = await checkValidation(element);
    if (isValid) {
      const addedObj = {
        code: element.code,
        description: element.description,
        type: element.type,
        image: element.image || null,
        fullImage: element.fullImage || null,
        removeImage: element.removeImage || false,
      };
      if (activeCareType === 'disclaimer') {
        addedObj.disclaimerType = element.disclaimerType;
      } else {
        addedObj.careType = element.careType;
      }

      setCareLabelState((prevState) => ({
        ...prevState,
        uploadingData: true,
      }));
      if (element?.newItem) {
        const res = await dispatch(action.addCareLibrary(addedObj, t));
        if (res.code === 200) {
          await resetData();
        }
        setCareLabelState((prevState) => ({
          ...prevState,
          uploadingData: false,
        }));
      } else {
        addedObj.loggedInUserId = userInfo?.userId;

        const res = await dispatch(action.editCareLibrary(addedObj, element?._id, t));
        if (res.code === 200) {
          await resetData();
        }
        setCareLabelState((prevState) => ({
          ...prevState,
          uploadingData: false,
        }));
      }
    }
  };

  const handleEdit = (editId, index) => {
    const dataList = CareLabelList;
    set(dataList, `[${index}][editMode]`, true);
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: dataList,
      editedId: editId,
    }));
  };

  const handleLibrarySearchText = async (librarySearchText) => {
    setCareLabelState((prevState) => ({
      ...prevState,
      libSearchText: librarySearchText,
    }));
  };

  const handleSearchSubmit = async (event) => {
    if (event.key === 'Enter') {
      // Perform your desired action here
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: [],
        pageIndex: 1,
      }));
      const typeObj = {
        searchText: libSearchText,
        type: careType,
        pageSize,
        pageIndex: 1,
      };
      await dispatch(action.fetchCareLabel(typeObj));
    }
  };

  const handlePageNextInScroll = async () => {
    if (CareLabelList?.length < totalCount) {
      const typeObj = {
        type: careType,
        pageSize,
        pageIndex: pageIndex + 1,
      };
      if (libSearchText !== '') {
        typeObj.searchText = libSearchText;
      }
      await dispatch(action.fetchCareLabel(typeObj));
      setCareLabelState((prevState) => ({
        ...prevState,
        pageIndex: pageIndex + 1,
      }));
    }
  };

  const contextValue = useMemo(
    () => ({
      careLabelState,
      setCareLabelState,
      initialState,
      getCurrentLibraryData,
      handleCheckAll,
      handleCheckLibrary,
      isallChecked,
      userInfo,
      handleAddLibrary,
      careTypeOption,
      disclaimerTypeOption,
      handleClose,
      handleInputChange,
      handleSelectChange,
      handleAddUpdate,
      handleEdit,
      getCareGroupData,
      handleLibrarySearchText,
      handleSearchSubmit,
      handlePageNextInScroll,
    }),
    [
      careLabelState,
      setCareLabelState,
      initialState,
      getCurrentLibraryData,
      handleCheckAll,
      handleCheckLibrary,
      isallChecked,
      userInfo,
      handleAddLibrary,
      careTypeOption,
      disclaimerTypeOption,
      handleClose,
      handleInputChange,
      handleSelectChange,
      handleAddUpdate,
      handleEdit,
      getCareGroupData,
      handleLibrarySearchText,
      handleSearchSubmit,
      handlePageNextInScroll,
    ],
  );
  return (
    <StateContext.Provider
      value={{
        ...contextValue,
      }}
    >
      {children}
    </StateContext.Provider>
  );
}

export function useCareLabelState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the CareLabelStateProvider');
  }
  return context;
}
